// ── ObserverBanner (P12.2 + P12.2a switcher + P12.2b act-as) ──
// Persistent banner shown when SuperAdmin observes / could observe a
// contractor or installer surface. Three modes:
//
//   1. pilotSwitchOnly      → SuperAdmin landed on the surface but is not
//                             observing yet. Renders the surface switcher.
//   2. observe (default)    → Amber, read-only. Switcher + Exit + "Act as".
//   3. act-as (escalated)   → Red, write-enabled. Countdown + End Act-As.
//
// Only renders when caller has gated by `isSuperAdmin`. Real
// installers/contractors NEVER see it.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, X, Unlock, Lock } from "lucide-react";
import {
  setObserverIdentity,
  type ObserverKind,
} from "@/data/observerMode/observerSession";
import { ActAsConfirmDialog } from "@/components/observerMode/ActAsConfirmDialog";
import type { PickerOption } from "@/components/observerMode/ObserverPicker";

interface SwitcherProps {
  currentKind: ObserverKind;
  currentId: string;
  crewTargetKind?: ObserverKind;
  contractorOptions?: PickerOption[];
  crewOptions?: PickerOption[];
}

interface Props extends Partial<SwitcherProps> {
  displayName: string;
  onExit: () => void;
  /** When true, render as a passive Pilot Switch (no Observing/Exit/Act-As). */
  pilotSwitchOnly?: boolean;
  /** Act-As state (P12.2b). When omitted, Act-As UI is hidden. */
  isActingAs?: boolean;
  actAsRemainingMs?: number;
  onStartActAs?: (reason: string) => boolean;
  onEndActAs?: () => void;
}

const SURFACE_PATH: Record<ObserverKind, string> = {
  contractor: "/contractor-portal",
  installer: "/installer-dashboard",
};

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function ObserverBanner({
  displayName,
  onExit,
  currentKind,
  currentId,
  crewTargetKind = "installer",
  contractorOptions,
  crewOptions,
  pilotSwitchOnly,
  isActingAs,
  actAsRemainingMs,
  onStartActAs,
  onEndActAs,
}: Props) {
  const navigate = useNavigate();
  const [actAsOpen, setActAsOpen] = useState(false);

  const hasSwitcher =
    !!currentKind &&
    ((contractorOptions && contractorOptions.length > 0) ||
      (crewOptions && crewOptions.length > 0));

  const jump = (kind: ObserverKind, id: string) => {
    if (!id) return;
    setObserverIdentity(kind, id);
    navigate(`${SURFACE_PATH[kind]}?observeAs=${encodeURIComponent(id)}`, {
      replace: true,
    });
  };

  const showActAsControls = !pilotSwitchOnly && !!onStartActAs;
  const tone = isActingAs
    ? "border-destructive/50 bg-destructive/15 text-destructive"
    : "border-amber-500/40 bg-amber-500/15 text-amber-900 dark:text-amber-100";

  return (
    <>
      <div
        data-testid="observer-banner"
        data-act-as={isActingAs ? "true" : "false"}
        role="status"
        aria-live="polite"
        className={`sticky top-0 z-[60] flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 ${tone}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isActingAs ? (
            <Unlock className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <Eye className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="text-[11px] font-semibold truncate">
            {pilotSwitchOnly
              ? "SuperAdmin · Pilot Switch — pick a contractor or crew to observe"
              : isActingAs
                ? `ACTING AS ${displayName} — writes go live (${formatRemaining(actAsRemainingMs ?? 0)})`
                : `Observing as ${displayName} — read-only`}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {hasSwitcher && contractorOptions && contractorOptions.length > 0 && (
            <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
              <span className="opacity-70">Contractor</span>
              <select
                data-testid="observer-switch-contractor"
                value={currentKind === "contractor" ? currentId : ""}
                onChange={(e) => jump("contractor", e.target.value)}
                className="rounded-md border border-current/40 bg-background/60 px-2 py-1 text-[11px] font-semibold text-foreground"
              >
                <option value="">— pick —</option>
                {contractorOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {hasSwitcher && crewOptions && crewOptions.length > 0 && (
            <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
              <span className="opacity-70">Crew</span>
              <select
                data-testid="observer-switch-crew"
                value={currentKind === crewTargetKind ? currentId : ""}
                onChange={(e) => jump(crewTargetKind, e.target.value)}
                className="rounded-md border border-current/40 bg-background/60 px-2 py-1 text-[11px] font-semibold text-foreground"
              >
                <option value="">— pick —</option>
                {crewOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showActAsControls && !isActingAs && (
            <button
              type="button"
              data-testid="act-as-open"
              onClick={() => setActAsOpen(true)}
              className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-background/60 px-2 py-1 text-[11px] font-semibold hover:bg-background"
            >
              <Unlock className="h-3 w-3" />
              Act as…
            </button>
          )}

          {showActAsControls && isActingAs && (
            <button
              type="button"
              data-testid="act-as-end"
              onClick={onEndActAs}
              className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-background/60 px-2 py-1 text-[11px] font-semibold hover:bg-background"
            >
              <Lock className="h-3 w-3" />
              End Act-As
            </button>
          )}

          {!pilotSwitchOnly && (
            <button
              type="button"
              data-testid="observer-exit"
              onClick={onExit}
              className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-background/60 px-2 py-1 text-[11px] font-semibold hover:bg-background"
            >
              <X className="h-3 w-3" />
              Exit
            </button>
          )}
        </div>
      </div>

      {showActAsControls && onStartActAs && (
        <ActAsConfirmDialog
          open={actAsOpen}
          observedLabel={displayName}
          onCancel={() => setActAsOpen(false)}
          onConfirm={(reason) => {
            const ok = onStartActAs(reason);
            if (ok) setActAsOpen(false);
            return ok;
          }}
        />
      )}
    </>
  );
}
